import { getLoggedUser } from "@/utils/sessionUtils";
import { apiFetch } from "./api";

export type LogLevel = 'info' | 'warn' | 'error' | 'critical';

export enum LogEventType {
  Created = 'entity_created',
  Updated = 'entity_updated',
  Deleted = 'entity_deleted',
  Activated = 'entity_activated',
  Deactivated = 'entity_deactivated'
}

export interface Log {
  id: string;
  created_at: string;
  event_type: string;
  level: LogLevel;
  actor_id: string;
  entity_name: string;
  entity_id: string;
  payload: string;
}

export interface LogList {
  id: string;
  created_at: string;
  event_type: string;
  level: LogLevel;
  username: string;
  actor_id: string;
  entity_name: string;
  entity_id: string;
  client_id: string | null;
  client_name: string | null;
  module_id: string | null;
  // Empresas do autor (do cliente do log, quando houver). A API devolve a
  // lista; a tela de logs a junta em texto para exibir na tabela.
  user_company: string[] | string;
  payload: string;
}

export interface CreateLogRequest {
  created_at?: string;
  event_type: string;
  level?: LogLevel;
  actor_id?: string;
  entity_name?: string;
  entity_id?: string;
  payload: Record<string, any>;
}

export type ListLogParams = {
  limit: number;
  offset: number;
  event_type?: string;
  level?: string;
  entity_name?: string;
  username?: string;
  date?: string; // formato YYYY-MM-DD
}

export type LogWithPages = {
  total: number,
  logs: LogList[]
}

export function getLogs(data: ListLogParams) {
  const params = new URLSearchParams();

  if (data.limit) params.set('limit', String(data.limit));
  if (data.offset) params.set('offset', String(data.offset));
  if (data.event_type) params.set('event_type', data.event_type);
  if (data.level) params.set('level', data.level);
  if (data.entity_name) params.set('entity_name', data.entity_name);
  if (data.username) params.set('username', data.username);
  if (data.date) params.set('date', data.date);

  const paramString = params.toString();
  return apiFetch<LogWithPages>(`/api/log${paramString ? "?" + paramString : ""}`);
}

// O cliente é lido da sessão no momento do envio (não na montagem da tela),
// para acompanhar a troca de cliente durante a sessão. O servidor grava o log
// sem cliente se o header não for enviado.
export function createLog(entry: CreateLogRequest) {
  try {
    // O servidor atribui o log ao cliente do header X-Client-ID; sem ele o log
    // só ganha cliente se o autor pertencer a um único cliente.
    const clientId = getLoggedUser()?.loggedClient?.id;
    return apiFetch("/api/log", {
      method: "POST",
      headers: clientId ? { "X-Client-ID": clientId } : undefined,
      body: JSON.stringify({
        ...entry,
        level: entry.level || 'info',
        created_at: entry.created_at || new Date().toISOString(),
      }),
    });
  } catch (e) {
    console.error("Failed to create log", e)
  }
}

// Indicador de uso do App do Dono (GET /api/log/usage): uma linha por
// cliente × usuário × módulo × dia, já agregada no banco.
export interface UsageRow {
  client_id: string;
  client_name: string;
  user_id: string;
  user_name: string;
  user_role: string;
  module_id: string;
  dia: string; // YYYY-MM-DD, horário de Brasília
  acessos: number; // aberturas do módulo (screen_view)
  eventos: number; // demais eventos registrados no módulo
  erros: number;
  ultimo_acesso: string;
}

export interface UsageResponse {
  de: string;
  ate: string;
  linhas: UsageRow[];
}

export type UsageParams = {
  client_id?: string;
  module_id?: string;
  de?: string; // YYYY-MM-DD
  ate?: string; // YYYY-MM-DD
};

export function getUsage(data: UsageParams = {}) {
  const params = new URLSearchParams();

  if (data.client_id) params.set('client_id', data.client_id);
  if (data.module_id) params.set('module_id', data.module_id);
  if (data.de) params.set('de', data.de);
  if (data.ate) params.set('ate', data.ate);

  const paramString = params.toString();
  return apiFetch<UsageResponse>(`/api/log/usage${paramString ? "?" + paramString : ""}`);
}
